import Fade from "@material-ui/core/Fade";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import React, { useState } from "react";
import styles from "./RowActionsMenu.module.scss";

export interface RowActionItem {
  /** Spanish label; also rendered as the aria-label of the menu item */
  label: string;
  icon: React.ReactNode;
  /**
   * Receives the trigger IconButton element (never the menu item) so that
   * popovers/dialogs can anchor to a stable element after the menu closes.
   */
  onClick: (triggerEl: HTMLElement) => void;
  /** Red text/icon like ItemActionsMenu deleteColor rgb(255, 86, 48) */
  danger?: boolean;
  disabled?: boolean;
}

export interface RowActionsMenuProps {
  items: RowActionItem[];
  triggerLabel?: string;
}

/**
 * Shared three-dot row action menu for table rows.
 * The menu is portal-rendered by MUI and unmounted while closed.
 */
export function RowActionsMenu({
  items,
  triggerLabel = "Acciones de fila",
}: RowActionsMenuProps) {
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorElement);

  if (items.length === 0) return null;

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleItemClick = (item: RowActionItem) => {
    if (item.disabled) return;
    // Capture the trigger before closing: menu items unmount on close.
    const triggerEl = anchorElement;
    handleClose();
    if (triggerEl) item.onClick(triggerEl);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={triggerLabel}
        aria-haspopup="true"
        onClick={handleTriggerClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        getContentAnchorEl={null}
        elevation={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: styles.paper }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.label}
            aria-label={item.label}
            disabled={item.disabled}
            className={item.danger ? styles.danger : undefined}
            onClick={() => handleItemClick(item)}
          >
            <span className={styles.itemContent}>
              {item.icon}
              <span className={styles.itemLabel}>{item.label}</span>
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default RowActionsMenu;
